import { PhaserGamePad } from "src/games/shared/Gamepad";
import bindAllOfThis from "src/utils/BindThisHelper";
import { Color } from "src/games/shared/ColorValue";
import { PlumbingObject } from "../objects/PlumbingObject";
import { PlumbingTool } from "../objects/PlumbingTool";
import { TiledGameObject } from "src/games/shared/phaserHelpers/HandyTypes";
import { PhaserModalConfig, PhaserModal } from "src/games/shared/Modal";
import TranslateApi from "src/utils/TranslateHelper";
import { EndGameScene } from "src/games/PlumbingGame/scenes/EndGameScene";

export class MainScene extends Phaser.Scene {
    gameWidth: number;
    gameHeight: number;
    doubleJump: boolean = false;
    frameCount: number = 0;
    gamepad: PhaserGamePad;
    groundLayer: Phaser.Tilemaps.StaticTilemapLayer;
    backgroundLayer: Phaser.Tilemaps.StaticTilemapLayer;
    pipeLayer: Phaser.Tilemaps.DynamicTilemapLayer;
    zoneLayer: Phaser.Tilemaps.ObjectLayer;
    player: Phaser.Physics.Arcade.Sprite;
    map: Phaser.Tilemaps.Tilemap;
    gamePad: PhaserGamePad;
    plumbingObjects: PlumbingObject[];
    plumbingTools: PlumbingTool[];
    currentPlumbingObject: PlumbingObject;
    currentTool: PlumbingTool;
    toolIndex: number;
    feedbackBorder: Phaser.GameObjects.Image;
    feedbackText: Phaser.GameObjects.Text;
    gameOver: boolean = false;
    pickingTool: boolean = false;

    private translateString = "Plumbing.Phaser.MainScene.";

    xVel: number = 0;
    yVel: number = 0;

    private TILESIZE: number = 32;
    private JUMPING_VELOCITY = 400;
    private WALKING_VELOCITY = 200;
    private CLOGGED_ALPHA = .2;
    private FIXED_ALPHA = 1;

    constructor() {
        super({
            key: "MainScene"
        });

        bindAllOfThis(this, MainScene.prototype);
    }

    preload(): void {
        // All preload assets will be stored in the texture manager by the given key
        this.load.tilemapTiledJSON('map', require("/src/assets/qci/maps/gamePlumbing/gamePlumbing2.json"));
        this.load.image('gridtiles', require("/src/assets/qci/maps/gamePlumbing/gridtiles.png"));
        this.load.image('walls-and-water', require("/src/assets/qci/maps/gamePlumbing/walls-and-water.png"));
        this.load.atlas("assets", require("/src/assets/qci/atlas/gamePlumbing.png"), require("/src/assets/qci/atlas/gamePlumbing.json"));
        this.load.spritesheet('player', require('src/assets/qci/sprites/studentPilot/student_spritemap-64x96.png'), { frameWidth: 64, frameHeight: 96 });

        PhaserGamePad.preload(this);
        PhaserModal.preload(this);
    }

    initialize(): void {
        this.gameWidth = this.textures.game.config.width as number;
        this.gameHeight = this.textures.game.config.height as number;
        this.gamepad = new PhaserGamePad(this);
        this.plumbingObjects = [];
        this.plumbingTools = PlumbingTool.TOOLS;
        this.initializeMap();
        this.currentPlumbingObject = null;
        this.currentTool = null;
        this.toolIndex = 0;
        this.pickingTool = false;
    }

    create(): void {
        this.initialize();
        this.createGamePad();
        this.generateScene();
        this.generatePlayer();
        this.initPolygonZones();
        this.createFeedbackBubble();
    }

    initializeMap(): void {
        this.map = this.make.tilemap({ key: 'map' });
        let bgTiles = this.map.addTilesetImage('gridtiles');
        let walls = this.map.addTilesetImage('walls-and-water');
        this.backgroundLayer = this.map.createStaticLayer('Background', bgTiles, 0, 0);
        this.backgroundLayer.setAlpha(0.75);
        this.pipeLayer = this.map.createDynamicLayer('PipesClogged', walls, 0, 0);
        this.groundLayer = this.map.createStaticLayer('Walls', walls, 0, 0);

        // Tile IDs with collision
        this.groundLayer.setCollisionBetween(0, 500);

        this.pipeLayer.layer.data.forEach((row: Phaser.Tilemaps.Tile[]) => {
            row.forEach(tile => {
                tile.setAlpha(this.CLOGGED_ALPHA);
            });
        });

        this.zoneLayer = this.map.getObjectLayer("Zones");
    }

    createFeedbackBubble(): void {
        this.feedbackBorder = this.add.image(0, 0, "pixel");
        this.feedbackBorder.setOrigin(0);
        this.feedbackBorder.setScrollFactor(0);
        this.feedbackBorder.setTint(Color.PLANET_COLORS.get("R"));
        this.feedbackBorder.setVisible(false);

        var textStyle = { fontFamily: 'Russo One, Arial', color: "#ffffff", fontSize: 20, backgroundColor: "#000000", padding: 20 };
        this.feedbackText = this.add.text(5, 5, "", textStyle);
        this.feedbackText.setScrollFactor(0);
        this.feedbackText.setWordWrapWidth(300, true);
        this.feedbackText.setVisible(false);
    }

    generatePlayer(): void {
        this.player = this.physics.add.sprite(this.TILESIZE * 38, this.TILESIZE * 37, "player");
        this.player.setBounce(0.1);
        this.player.setSize(50, 80);
        this.player.setOffset(6, 15);

        //  Player animations, turning, walking left and walking right.
        this.anims.create({
            key: 'left',
            frames: this.anims.generateFrameNumbers('player', { start: 0, end: 3 }),
            frameRate: 10,
            repeat: -1
        });

        this.anims.create({
            key: 'turn',
            frames: [{ key: 'player', frame: 4 }],
            frameRate: 20
        });

        this.anims.create({
            key: 'right',
            frames: this.anims.generateFrameNumbers('player', { start: 5, end: 8 }),
            frameRate: 10,
            repeat: -1
        });

        this.physics.add.collider(this.player, this.groundLayer);

        // Setup the camera
        this.cameras.main.setBounds(0, 0, this.map.widthInPixels, this.map.heightInPixels);
        this.cameras.main.startFollow(this.player);
    }

    generateScene(): void {
        this.addCloggedObjects();
        this.showUncloggedPipes();
    }

    addCloggedObjects(): void {
        this.map.getObjectLayer("Objects").objects.forEach((ob: TiledGameObject) => {
            var id = ob.properties["id"];
            var zoneID = ob.properties["zoneID"];
            var dependentOn = ob.properties["dependentOn"].split(',');
            if (ob.name === "toilet") {
                var toilet = new PlumbingObject(id, "toilet", "toilet_pipe_broken", "toilet_pipe_fixed", zoneID, dependentOn);
                var toiletObject = this.add.sprite(ob.x, ob.y, "assets", toilet.cloggedFrame);
                toiletObject.setOrigin(0);
                toiletObject.setDisplaySize(ob.width, ob.height);
                toilet.object = toiletObject;
                this.plumbingObjects.push(toilet);
            }
            else if (ob.name === "tub") {
                var tub = new PlumbingObject(id, "tub", "tub_not-fixed", "tub_fixed", zoneID, dependentOn);
                var tubObject = this.add.sprite(ob.x, ob.y, "assets", tub.cloggedFrame);
                tubObject.setOrigin(0);
                tubObject.setDisplaySize(ob.width, ob.height);
                tub.object = tubObject;
                this.plumbingObjects.push(tub);
            }
            else if (ob.name === "sink") {
                var sink = new PlumbingObject(id, "sink", "sink_broken", "sink_fixed", zoneID, dependentOn);
                var sinkObject = this.add.sprite(ob.x, ob.y, "assets", sink.cloggedFrame);
                sinkObject.setOrigin(0);
                sinkObject.setDisplaySize(ob.width, ob.height);
                sink.object = sinkObject;
                this.plumbingObjects.push(sink);
            }
        });
    }

    showUncloggedPipes(): void {
        this.map.getObjectLayer("Unclogged").objects.forEach((ob: TiledGameObject) => {
            ob.polygon.forEach((point: Phaser.Geom.Point) => {
                point.x += ob.x;
                point.y += ob.y;
            });

            var polygon = new Phaser.Geom.Polygon(ob.polygon);

            this.pipeLayer.layer.data.forEach((row: Phaser.Tilemaps.Tile[]) => {
                row.forEach(tile => {
                    if (polygon.contains(tile.pixelX, tile.pixelY)) {
                        tile.setAlpha(this.FIXED_ALPHA);
                    }
                });
            });
        });
    }

    isPlayerNextToPlumbingObject(object: Phaser.GameObjects.Sprite): boolean {
        var objectArea = object.getBounds();
        objectArea.width = 150;
        objectArea.centerX = object.getCenter().x;
        return Phaser.Geom.Intersects.RectangleToRectangle(this.player.getBounds(), objectArea);
    }

    /**
    * Fixes the plumbing object
    */
    fixPlumbingObject(): void {
        // Correct tool to fix this object
        if (this.currentTool.name === this.currentPlumbingObject.toolName) {
            this.currentPlumbingObject.object.setTexture("assets", this.currentPlumbingObject.fixedFrame);
            this.currentPlumbingObject.isClogged = false;
            this.updateObjectPipeZone(this.currentPlumbingObject, this.FIXED_ALPHA);

            if (this.needToReclogDependents()) {
                this.reclogDependents();
                // Would be nice to show an animation here if possible (to show they messed up)
                this.showFeedback(TranslateApi.translate(this.translateString + "oopsText"));
            }
            else {
                this.showFeedback(TranslateApi.translate(this.translateString + "fixedText" + Phaser.Math.Between(1, 3)));
            }
            this.currentPlumbingObject = null;
            this.currentTool.object.destroy();
            this.currentTool = null;
            // Re-enable the player
            this.player.enableBody(false, this.player.x, this.player.y, true, true);
        }
        else {
            if (!this.tweens.isTweening(this.currentTool.object)) {
                this.wrongToolTween();
            }
        }
    }

    wrongToolTween(): void {
        this.tweens.add({
            name: 'tool',
            targets: this.currentTool.object,
            x: this.currentTool.object.x + 10,
            ease: "Linear",
            duration: 40,
            repeat: 3,
            yoyo: true,
            onComplete: () => {
                if (this.currentTool != null) {
                    this.currentTool.object.x = this.player.getCenter().x - this.currentTool.object.displayWidth / 2;
                }
            }
        });
    }

    /**
     * Checks we need to reclog objects below this one because it was done out of order
     */
    needToReclogDependents(): boolean {
        // Not dependent on anything, no problem
        if (this.currentPlumbingObject.dependentOn.length === 0) {
            return false;
        }
        else {
            var dependents = this.getAllDependents();
            var dependent: PlumbingObject;
            for (dependent of dependents) {
                if (dependent.isClogged) {
                    // There's a dependent that's not unclogged, so have to reclog all dependents
                    return true;
                }
            }
            return false;
        }
    }

    reclogDependents(): void {
        var dependents = this.getAllDependents();
        // Reclog current object first
        dependents.splice(0, 0, this.currentPlumbingObject);
        dependents.forEach((po: PlumbingObject) => {
            po.isClogged = true;
            po.object.setFrame(po.cloggedFrame);
            // Update the pipes for that zone to show them with no flow now
            this.updateObjectPipeZone(po, this.CLOGGED_ALPHA);
        });
    }

    getAllDependents(): PlumbingObject[] {
        var dependents = [];
        this.currentPlumbingObject.dependentOn.forEach((id: number) => {
            var obj = this.plumbingObjects.find((po: PlumbingObject) => {
                return po.id === id;
            });
            dependents.push(obj);
        });
        return dependents;
    }

    showFeedback(text: string) {
        if (!this.feedbackText.visible) {
            this.feedbackText.setText(text);
            this.feedbackBorder.setDisplaySize(this.feedbackText.displayWidth + 10, this.feedbackText.displayHeight + 10);

            this.feedbackBorder.setVisible(true);
            this.feedbackText.setVisible(true);

            var playerX = Math.min(this.player.x - this.cameras.main.scrollX - this.feedbackBorder.displayWidth / 2 + 5, this.gameWidth - this.feedbackBorder.displayWidth - 15);
            var playerY = this.player.getTopRight().y - this.cameras.main.scrollY - this.feedbackBorder.displayHeight - 10;

            this.feedbackText.setPosition(playerX + 5, playerY + 5);
            this.feedbackBorder.setPosition(playerX, playerY);
            this.delayHide();
        }
    }

    /**
     * Alternative way to make something disappear after a set time (rather than settimeout)
     */
    delayHide(): void {
        if (!this.tweens.isTweening(this.feedbackBorder)) {
            this.tweens.add({
                targets: [this.feedbackBorder, this.feedbackText],
                duration: 3500,
                ease: "Linear",
                x: "+=0",
                onComplete: () => {
                    this.hideFeedback();
                }
            });
        }
    }

    hideFeedback(): void {
        this.feedbackBorder.setVisible(false);
        this.feedbackText.setVisible(false);
    }

    showToolPicker(): void {
        // Have to be on the floor to bring up tool picker
        if (!this.player.body.onFloor()) {
            return;
        }
        this.pickingTool = true;

        if (this.feedbackText.visible) {
            this.hideFeedback();
        }

        this.player.anims.play("turn");
        if (this.currentTool != null) {
            this.currentTool.object.destroy();
        }
        this.currentTool = this.plumbingTools[this.toolIndex % this.plumbingTools.length];
        var tool = this.add.sprite(this.player.getCenter().x - this.currentTool.width / 2, this.player.getTopLeft().y - 50, "assets", this.currentTool.frame);
        tool.setOrigin(0);
        tool.setDisplaySize(this.currentTool.width, this.currentTool.height);
        this.currentTool.object = tool;
        this.toolIndex++;
    }

    /**
     * Set up polygon zones by adjusting their position from the map
     */
    initPolygonZones(): void {
        this.plumbingObjects.forEach((po: PlumbingObject) => {
            // Find the corresponding zone
            var zone = this.zoneLayer.objects.find((item: TiledGameObject) => {
                var zoneID = parseInt(item.name);
                return po.zoneID === zoneID;
            }) as TiledGameObject;

            // Make the points relative to the current object's position
            zone.polygon.forEach((point: Phaser.Geom.Point) => {
                point.x += zone.x;
                point.y += zone.y;
            });
        });
    }

    /**
     * Update the pipes to show them getting unclogged
     */
    updateObjectPipeZone(plumbingObject: PlumbingObject, alpha: number): void {
        // Find the corresponding zone
        var zone = this.zoneLayer.objects.find((item: TiledGameObject) => {
            var zoneID = parseInt(item.name);
            return plumbingObject.zoneID === zoneID;
        }) as TiledGameObject;

        // If the piping is in the zone, change the alpha to reflect it getting unclogged and having normal flow
        var polygon = new Phaser.Geom.Polygon(zone.polygon);

        this.pipeLayer.layer.data.forEach((row: Phaser.Tilemaps.Tile[]) => {
            row.forEach((tile: Phaser.Tilemaps.Tile) => {
                if (polygon.contains(tile.pixelX, tile.pixelY)) {
                    tile.setAlpha(alpha);
                }
            });
        });
    }

    isGameOver(): void {
        var object: PlumbingObject;
        // Check that everything is unclogged and that everything was fixed in the correct order
        for (object of this.plumbingObjects) {
            if (object.isClogged) {
                return;
            }
        }

        // Game is over!
        this.gameOver = true;
        this.player.disableBody();

        setTimeout(() => {
            var modalConfig = new PhaserModalConfig();
            var doneText = TranslateApi.translate(this.translateString + "doneText");
            modalConfig.title = doneText;
            modalConfig.buttonWidth = 200;
            modalConfig.buttonConfig.smartSize = true;
            var modal = new PhaserModal(this, 0, 0, [{ id: 1, displayText: TranslateApi.translate(this.translateString + "doneButton") }], modalConfig);
            modal.setPosition(this.gameWidth / 2 - modal.width / 2, this.gameHeight / 2 - modal.height / 2);
            modal.on('optionClicked', () => {
                modal.destroy();
                this.input.removeAllListeners();
                this.input.setDefaultCursor("default");
                this.scene.add("EndGameScene", EndGameScene, true);
                this.scene.remove(this);
            });
        }, 1000);

    }

    update(): void {
        if (!this.gameOver) {
            this.updatePlayerMovement();
            this.isGameOver();

            // Abandoning the tool picker
            if (this.pickingTool && this.currentPlumbingObject != null && !this.isPlayerNextToPlumbingObject(this.currentPlumbingObject.object)) {
                this.currentTool.object.destroy();
                this.currentTool = null;
                this.currentPlumbingObject = null;
                this.pickingTool = false;
            }
        }
    }

    /**
    * GamePad/Player Movement 
    * EVENT BASED FOR JUMPING, UPDATE BASED FOR LEFT/RIGHT
    */
    createGamePad(): void {
        this.gamePad = new PhaserGamePad(this);
        this.gamePad.on("keydown_up", this.playerJump);
        this.gamePad.on("keydown_a", this.aDown);
        this.gamePad.on("keydown_b", this.bDown);
    }

    updatePlayerMovement(): void {
        if (!this.player.body.enable) {
            return;
        }
        this.xVel = 0;

        if (this.gamePad.keys.left.isDown) {
            this.moveLeft();
        }
        if (this.gamePad.keys.right.isDown) {
            this.moveRight();
        }

        this.player.setVelocityX(this.xVel);

        if (this.xVel === 0) {
            this.player.anims.play('turn');
        }

        if (this.frameCount % 4 === 0) {
            if (this.player.body.onFloor()) {
                this.doubleJump = true;
            }
        }
        if (this.frameCount > 64000) {
            this.frameCount = 0;
        }
        this.frameCount++;

        if (this.pickingTool && this.currentTool != null) {
            this.currentTool.object.x = this.player.getCenter().x - this.currentTool.width / 2;
            this.currentTool.object.y = this.player.getTopLeft().y - 50;
        }
    }

    /**
    * A/Spacebar is used to select a tool 
    */
    aDown() {
        if (this.currentPlumbingObject == null) {
            // Find the first clogged plumbing object next to the player and show the tool picker
            var plumbingObject: PlumbingObject;
            for (plumbingObject of this.plumbingObjects) {
                if (this.isPlayerNextToPlumbingObject(plumbingObject.object) && plumbingObject.isClogged) {
                    this.currentPlumbingObject = plumbingObject;
                    this.showToolPicker();
                    break;
                }
            }
        }
        else {
            this.showToolPicker();
        }
    }

    /**
     * B/Shift is used to fix the object with the current tool
     */
    bDown() {
        // Select to use the tool to fix the object
        if (this.currentTool != null) {
            this.currentTool.usingTool = true;
            this.fixPlumbingObject();
        }
    }

    playerJump() {
        if (this.player.body.onFloor() && this.player.body.enable) {
            this.player.setVelocityY(-this.JUMPING_VELOCITY);
        } else if (this.doubleJump) {
            this.player.setVelocityY(-this.JUMPING_VELOCITY);
            this.doubleJump = false;
        }
    }

    moveLeft() {
        if (this.player.body.enable) {
            this.xVel += -this.WALKING_VELOCITY;
            this.player.anims.play('left', true);
        }
    }

    moveRight() {
        if (this.player.body.enable) {
            this.xVel += this.WALKING_VELOCITY;
            this.player.anims.play('right', true);
        }
    }

}



// WEBPACK FOOTER //
// ./src/games/PlumbingGame/scenes/MainScene.ts